/**
 * CoordinatorDashboard.js
 *
 * Device Coordinator dashboard for Coordinator role
 * Shows: device inventory, device status, maintenance schedule, device compliance, complaints, alerts
 * Scope: Department-scoped (their assigned department only)
 * Financial data: NOT visible
 *
 * Shows 6 sections:
 * 1. Quick Statistics - Device counts, status, alerts
 * 2. Critical Maintenance Alerts - Overdue and urgent maintenance
 * 3. Maintenance Due - Upcoming PPM, calibration, and compliance
 * 4. Devices - Inventory snapshot, reliability, age, and a recent-device preview
 * 5. Device Compliance - PPM, calibration, and compliance
 * 6. Recent Activity - Device management activities
 *
 * Reference: DASHBOARD_MODULAR_ARCHITECTURE_PLAN.md Phase 6, Task 6.5
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../auth/hooks/useAuth';
import BaseDashboard from './BaseDashboard';
import DashboardLayout, { Section } from './DashboardLayout';
import EmptyState from '../widgets/Shared/EmptyState';
import { DeviceIDBadge } from '../../../shared/components/common/Badges';
import { getMaintenanceTypeLabel as getLabel, getMaintenanceTypeClassName as getClassName } from '../../devices/utils/maintenanceUtils';
import CalendarView from '../../devices/components/CalendarView';
import DeviceMaintenanceScheduleModal, { deviceHasMaintenanceSchedule } from '../../devices/components/DeviceMaintenanceScheduleModal';
import DepartmentRecentActivity from '../widgets/Lists/DepartmentRecentActivity';
import StatsCard from '../../../shared/components/common/StatsCard';
import { formatFleetMetricDays } from '../../../utils/fleetMetricUtils';
import { formatPercentage } from '../../../utils/numberUtils';
import './Dashboards.css';

/**
 * CoordinatorDashboard Component
 *
 * Displays device management dashboard for coordinator staff.
 * Department-scoped data with no financial information.
 *
 * Props:
 * - token: JWT authentication token (optional, uses auth context if not provided)
 * - user: Current user object (optional, uses auth context if not provided)
 * - onNavigateToDevicesWithFilter: Callback to navigate to devices view with filters
 * - onNavigateToComplaintsWithFilter: Callback to navigate to complaints view
 * - onNavigateToDeviceView: Callback to navigate to device detail view
 *
 * Automatically uses authenticated user's role from auth context
 */
const CoordinatorDashboard = ({ token: providedToken, user: providedUser, onNavigateToDevicesWithFilter, onNavigateToComplaintsWithFilter, onNavigateToDeviceView, onNavigateToMaintenanceHistory, onNavigateToActivityLog }) => {
  // Get authenticated user's role and token from context
  const { token: contextToken, user: contextUser } = useAuth();
  const token = providedToken || contextToken;
  const user = providedUser || contextUser;
  const [scheduleDevice, setScheduleDevice] = useState(null);
  const [isCompactQuickNav, setIsCompactQuickNav] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const sync = () => setIsCompactQuickNav(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const getMaintenanceDueRowClass = (daysUntilDue) => {
    if (daysUntilDue === null) return 'status-unknown';
    if (daysUntilDue < 0) return 'status-overdue';
    if (daysUntilDue === 0) return 'status-due-today';
    if (daysUntilDue <= 7) return 'status-due-soon';
    return 'status-upcoming';
  };

  const formatMaintenanceDueRelative = (daysUntilDue) => {
    if (daysUntilDue === null) return 'N/A';
    if (daysUntilDue < 0) {
      const days = Math.abs(daysUntilDue);
      return `${days} day${days !== 1 ? 's' : ''} overdue`;
    }
    if (daysUntilDue === 0) return 'Due today';
    if (daysUntilDue === 1) return '1 day left';
    return `${daysUntilDue} days left`;
  };

  // Calculate device health status based on related data
  const getDeviceHealthStatus = (device) => {
    // Critical: Has overdue maintenance or non-compliant or active complaints
    if (device.maintenance_overdue || device.non_compliant || device.active_complaint_count > 0) {
      return { status: 'critical', icon: '🔴', label: 'Critical' };
    }

    // Warning: Has maintenance due soon (within 7 days) or audit/calibration due soon
    if (device.days_until_due !== undefined && device.days_until_due <= 7 && device.days_until_due >= 0) {
      return { status: 'warning', icon: '🟡', label: 'Warning' };
    }

    // Healthy: No issues detected
    return { status: 'healthy', icon: '🟢', label: 'Healthy' };
  };

  return (
    <BaseDashboard
      user={user}
      token={token}
    >
      {(apiResponse) => {
        // Extract dashboard data from API response
        const dashboardData = apiResponse?.data || {};

        const inventory_stats = dashboardData?.inventory_stats || {};
        const device_status = dashboardData?.device_status || {};
        const maintenance_alerts = dashboardData?.maintenance_alerts || [];
        const maintenance_due = dashboardData?.maintenance_due || [];
        const device_compliance = dashboardData?.device_compliance || {};
        const device_list = dashboardData?.device_list || [];
        const recent_activity = dashboardData?.recent_activity || [];
        const recent_activity_summary = dashboardData?.recent_activity_summary || null;

        return (
          <>
          <DashboardLayout columns="1" gap="md">
            {/* Quick Navigation Links */}
            <div
              className={`admin-quick-nav${isCompactQuickNav ? ' admin-quick-nav--compact' : ''}`}
            >
              <div style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '0.75rem', color: '#6b7280' }}>
                📍 Quick Navigation
              </div>
              <div className="admin-quick-nav-scroll">
                <div
                  className="admin-quick-nav-links"
                  style={{
                    display: 'flex',
                    flexWrap: isCompactQuickNav ? 'nowrap' : 'wrap',
                    gap: '0.5rem'
                  }}
                >
                  {[
                    { label: '📊 Statistics', id: 'quick-statistics' },
                    { label: '🚨 Alerts', id: 'maintenance-alerts' },
                    { label: '🔧 Maintenance', id: 'maintenance-due' },
                    { label: '🖥️ Devices', id: 'device-status' },
                    { label: '✅ Compliance', id: 'device-compliance' },
                    { label: '📊 Activity', id: 'recent-activity' }
                  ].map((link) => (
                    <a
                      key={link.id}
                      href={`#${link.id}`}
                      className="admin-quick-nav-link"
                      style={{
                        padding: '0.5rem 0.75rem',
                        backgroundColor: '#f3f4f6',
                        border: '1px solid #e5e7eb',
                        borderRadius: '4px',
                        fontSize: '0.85rem',
                        color: 'var(--dp-accent-primary)',
                        textDecoration: 'none',
                        fontWeight: '500',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = 'var(--dp-accent-primary)';
                        e.target.style.color = '#fff';
                        e.target.style.borderColor = 'var(--dp-accent-primary)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#f3f4f6';
                        e.target.style.color = 'var(--dp-accent-primary)';
                        e.target.style.borderColor = '#e5e7eb';
                      }}
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* ========================================
                SECTION 1: QUICK STATISTICS
                Device counts and alert status
                ======================================== */}
            <Section id="quick-statistics" title="📊 Quick Statistics">
              <div className="list-default-window-note" role="note">
                <span className="list-default-window-note__icon" aria-hidden="true">ℹ️</span>
                <span>
                  Device cards match the Devices list for this department (excludes drafts and pending approval).
                  Critical Issues is open critical complaints (any age).
                </span>
              </div>
              <div className="dashboard-stats-summary">
                {[
                  {
                    label: 'Total Devices',
                    value: inventory_stats.total_devices || 0,
                    icon: '📱',
                    className: 'total',
                    action: 'view-all-devices',
                    title: 'Click to view all devices',
                  },
                  {
                    label: 'Operational',
                    value: inventory_stats.operational_devices || 0,
                    icon: '✅',
                    className: 'active',
                    action: 'operational-devices',
                    title: 'Devices with no active complaints. Click to view.',
                  },
                  {
                    label: 'With Complaints',
                    value: inventory_stats.devices_under_maintenance || 0,
                    icon: '🔧',
                    className: 'maintenance',
                    action: 'devices-with-complaints',
                    title: 'Devices with an open complaint. Click to view.',
                  },
                  {
                    label: 'Critical Issues',
                    value: inventory_stats.critical_devices || 0,
                    icon: '🔴',
                    className: 'uncovered',
                    action: 'critical-only',
                    elevateWhenPositive: true,
                    title: 'Open critical complaints. Click to view.',
                  },
                  {
                    label: 'Overdue Maint.',
                    value: inventory_stats.maintenance_overdue || 0,
                    icon: '⚠️',
                    className: 'open',
                    action: 'overdue-maint',
                    elevateWhenPositive: true,
                    title: 'Devices with overdue PPM, calibration, or compliance. Click to view.',
                  },
                ].map((stat) => {
                  const handleStatClick = () => {
                    switch (stat.action) {
                      case 'view-all-devices':
                        onNavigateToDevicesWithFilter && onNavigateToDevicesWithFilter({});
                        break;
                      case 'operational-devices':
                        onNavigateToDevicesWithFilter && onNavigateToDevicesWithFilter({
                          hasNoActiveComplaints: true,
                          hasActiveComplaints: false,
                          deviceStatus: [],
                        });
                        break;
                      case 'devices-with-complaints':
                        onNavigateToDevicesWithFilter && onNavigateToDevicesWithFilter({
                          hasActiveComplaints: true,
                          hasNoActiveComplaints: false,
                        });
                        break;
                      case 'critical-only':
                        onNavigateToComplaintsWithFilter && onNavigateToComplaintsWithFilter({
                          statuses: ['open', 'acknowledged', 'in_progress', 'escalated_to_vendor'],
                          severities: ['critical'],
                        });
                        break;
                      case 'overdue-maint':
                        onNavigateToDevicesWithFilter && onNavigateToDevicesWithFilter({
                          ppmStatus: ['Overdue (0-30d)', 'Overdue (30+d)'],
                          calibrationStatus: ['Overdue (0-30d)', 'Overdue (30+d)'],
                          complianceStatus: ['Overdue (0-30d)', 'Overdue (30+d)'],
                        });
                        break;
                      default:
                        break;
                    }
                  };

                  const elevate = stat.elevateWhenPositive && Number(stat.value) > 0;

                  return (
                    <StatsCard
                      key={stat.label}
                      icon={stat.icon}
                      number={stat.value}
                      label={stat.label}
                      className={`${stat.className}${elevate ? ' alert-elevated' : ''}`.trim()}
                      onClick={handleStatClick}
                      title={stat.title || `Click to view ${stat.label.toLowerCase()}`}
                    />
                  );
                })}
              </div>
            </Section>

            {/* ========================================
                SECTION 2: CRITICAL MAINTENANCE ALERTS
                Overdue and urgent maintenance
                ======================================== */}
            <Section id="maintenance-alerts" title={`🚨 Critical Maintenance Alerts${maintenance_alerts.length > 0 ? ` (${maintenance_alerts.length})` : ''}`}>
              {maintenance_alerts.length > 0 ? (
                <div className="maintenance-alerts-list">
                  {maintenance_alerts.map((alert, idx) => {
                    // Look up device ID from device_list since maintenance_alerts items might not include it
                    const device = device_list.find(d => d.device_identifier === alert.device_identifier);
                    const deviceIdToUse = device?.id || device?.device_id;
                    const severityClass = alert.severity === 'critical' ? 'critical' : 'warning';

                    const handleAlertClick = () => {
                      if (deviceIdToUse) {
                        onNavigateToDeviceView && onNavigateToDeviceView(deviceIdToUse);
                      }
                    };

                    return (
                      <div
                        key={idx}
                        className={`maintenance-alert-row clickable ${severityClass}`}
                        onClick={handleAlertClick}
                        title="Click to view device"
                      >
                        <div className="maintenance-alert-main">
                          {alert.device_identifier && (
                            <DeviceIDBadge deviceId={alert.device_identifier} size="small" />
                          )}
                          <span>{alert.device_name || 'Unknown Device'}</span>
                          <span className="maintenance-alert-sep">·</span>
                          <span className="maintenance-alert-type">{alert.maintenance_type || 'Maintenance'}</span>
                        </div>
                        <div className="maintenance-alert-meta">
                          {alert.days_overdue ? (
                            <span className="maintenance-alert-overdue">{alert.days_overdue}d overdue</span>
                          ) : null}
                          <span className="maintenance-alert-due">
                            Due: {alert.due_date ? new Date(alert.due_date).toLocaleDateString() : 'N/A'}
                          </span>
                          <span className={`maintenance-alert-severity ${severityClass}`}>
                            {alert.severity || 'Alert'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <EmptyState
                  section="Critical Maintenance Alerts"
                  message="No critical maintenance alerts. All devices are on schedule."
                  icon="🚨"
                />
              )}
            </Section>

            {/* ========================================
                SECTION 3: MAINTENANCE DUE
                Upcoming PPM, calibration, and compliance (next 30 days)
                ======================================== */}
            <Section id="maintenance-due" title={`🔧 Maintenance Due${maintenance_due.length > 0 ? ` (${maintenance_due.length})` : ''}`}>
              <div className="list-default-window-note" role="note">
                <span className="list-default-window-note__icon" aria-hidden="true">ℹ️</span>
                <span>
                  PPM, calibration, and compliance due from today through the next 30 days.
                  Click a row to open the device.
                </span>
              </div>
              {maintenance_due.length > 0 ? (
                <div className="maintenance-table-container maintenance-due-table">
                  <table className="maintenance-table">
                    <thead>
                      <tr>
                        <th className="col-device">Device</th>
                        <th className="col-device-id">Device ID</th>
                        <th className="col-type">Type</th>
                        <th className="col-department">Department</th>
                        <th className="col-location">Physical Location</th>
                        <th className="col-due-date">Due</th>
                      </tr>
                    </thead>
                    <tbody>
                      {maintenance_due.map((item, idx) => {
                        const daysUntilDue = item.days_until_due;
                        const rowClass = getMaintenanceDueRowClass(daysUntilDue);
                        const device = device_list.find(d => d.device_identifier === item.device_identifier);
                        const deviceIdToUse = item.id || item.device_id || device?.id || device?.device_id;

                        const handleRowClick = () => {
                          if (deviceIdToUse) {
                            onNavigateToDeviceView && onNavigateToDeviceView(deviceIdToUse);
                          }
                        };

                        return (
                          <tr
                            key={`${item.device_identifier}-${item.maintenance_type}-${item.due_date}-${idx}`}
                            className={`maintenance-row ${rowClass}${deviceIdToUse ? ' clickable' : ''}`}
                            onClick={handleRowClick}
                            title={deviceIdToUse ? 'Click to view device' : undefined}
                          >
                            <td className="col-device">
                              <span className="device-name">{item.device_name || 'Unknown Device'}</span>
                            </td>
                            <td className="col-device-id">
                              {item.device_identifier ? (
                                <DeviceIDBadge deviceId={item.device_identifier} size="small" />
                              ) : (
                                'N/A'
                              )}
                            </td>
                            <td className="col-type">
                              <span className="maintenance-type-badge" data-type={item.maintenance_type?.toLowerCase()}>
                                {item.maintenance_type?.toUpperCase() || 'N/A'}
                              </span>
                            </td>
                            <td className="col-department">
                              <span className="department-cell">
                                <span className="department-icon-small" aria-hidden="true">
                                  {item.department_icon || '🏢'}
                                </span>
                                <span className="department-text">{item.department || 'N/A'}</span>
                              </span>
                            </td>
                            <td className="col-location">{item.location || 'N/A'}</td>
                            <td className="col-due-date">
                              <div className="due-inline">
                                <span className="due-date-value">
                                  {item.due_date ? new Date(item.due_date).toLocaleDateString() : 'N/A'}
                                </span>
                                <span className={`days-left-bold${daysUntilDue !== null && daysUntilDue < 7 ? ' urgent' : ''}`}>
                                  {`· ${formatMaintenanceDueRelative(daysUntilDue)}`}
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState
                  section="Maintenance Due"
                  message="No maintenance scheduled for the next 30 days."
                  icon="🔧"
                />
              )}

              <div className="dashboard-subsection maintenance-due-calendar">
                <CalendarView
                  variant="compact"
                  token={token}
                  departmentId={user?.department_ids?.length === 1 ? user.department_ids[0] : null}
                  locationId={user?.location_id}
                  onNavigateToDevices={() => {
                    if (onNavigateToDevicesWithFilter) {
                      onNavigateToDevicesWithFilter({});
                    }
                  }}
                  onViewDevice={(deviceId) => {
                    if (onNavigateToDeviceView) {
                      onNavigateToDeviceView(deviceId);
                    }
                  }}
                />
              </div>
            </Section>

            {/* ========================================
                SECTION 4: DEVICES
                Snapshot + recent-device preview (not the Devices page)
                ======================================== */}
            <Section id="device-status" title="🖥️ Devices">
              {device_status && Object.keys(device_status).length > 0 ? (
                <>
                  <div className="dashboard-subsection">
                    <div className="dashboard-subsection-title">Snapshot</div>
                    <div className="list-default-window-note" role="note">
                      <span className="list-default-window-note__icon" aria-hidden="true">ℹ️</span>
                      <span>
                        Current inventory matching the Devices list (excludes drafts and pending approval).
                        Due in 30 days and Overdue count a device if any of PPM, calibration, or compliance matches.
                        Click a card to open the matching list.
                      </span>
                    </div>
                    <div className="dashboard-stats-summary">
                      {[
                        {
                          label: 'Operational',
                          value: device_status.operational || 0,
                          icon: '✅',
                          className: 'active',
                          filter: { hasNoActiveComplaints: true, hasActiveComplaints: false, deviceStatus: [] },
                          title: 'Devices with no active complaints. Click to view.',
                        },
                        {
                          label: 'With Complaints',
                          value: device_status.down || 0,
                          icon: '🔧',
                          className: 'maintenance',
                          filter: { hasActiveComplaints: true, hasNoActiveComplaints: false },
                          title: 'Devices with an open complaint. Click to view.',
                        },
                        {
                          label: 'On Schedule',
                          value: device_status.on_schedule || 0,
                          icon: '📅',
                          className: 'total',
                          title: 'PPM, calibration, and compliance all more than 30 days out.',
                        },
                        {
                          label: 'Due in 30 days',
                          value: device_status.due_soon || 0,
                          icon: '📆',
                          className: 'pending',
                          filter: {
                            ppmStatus: ['Due - Expiring Soon (≤30d)'],
                            calibrationStatus: ['Due - Expiring Soon (≤30d)'],
                            complianceStatus: ['Due - Expiring Soon (≤30d)'],
                          },
                          title: 'PPM, calibration, or compliance due within 30 days. Click to view.',
                        },
                        {
                          label: 'Overdue',
                          value: device_status.overdue || 0,
                          icon: '⚠️',
                          className: 'open',
                          elevateWhenPositive: true,
                          filter: {
                            ppmStatus: ['Overdue (0-30d)', 'Overdue (30+d)'],
                            calibrationStatus: ['Overdue (0-30d)', 'Overdue (30+d)'],
                            complianceStatus: ['Overdue (0-30d)', 'Overdue (30+d)'],
                          },
                          title: 'Overdue PPM, calibration, or compliance. Click to view.',
                        },
                      ].map((stat) => {
                        const elevate = stat.elevateWhenPositive && Number(stat.value) > 0;
                        return (
                          <StatsCard
                            key={stat.label}
                            icon={stat.icon}
                            number={stat.value}
                            label={stat.label}
                            className={`${stat.className}${elevate ? ' alert-elevated' : ''}`.trim()}
                            onClick={stat.filter && onNavigateToDevicesWithFilter
                              ? () => onNavigateToDevicesWithFilter(stat.filter)
                              : undefined}
                            title={stat.title}
                          />
                        );
                      })}
                    </div>
                  </div>
                  <div className="dashboard-subsection">
                    <div className="dashboard-subsection-title">Reliability</div>
                    <div className="list-default-window-note" role="note">
                      <span className="list-default-window-note__icon" aria-hidden="true">ℹ️</span>
                      <span>
                        {device_status.observation_window?.label || 'Last 12 months'} — same engine as Device View.
                        Availability is fleet average of impacting downtime. MTBF needs at least two impacting breakdowns on a device.
                      </span>
                    </div>
                    <div className="dashboard-stats-summary">
                      <StatsCard
                        icon="📶"
                        number={device_status.avg_uptime != null ? `${Number(device_status.avg_uptime).toFixed(1)}%` : 'N/A'}
                        label="Average Uptime"
                        className="active"
                        title="Fleet average availability from impacting downtime in the last 12 months."
                      />
                      <StatsCard
                        icon="⏱️"
                        number={formatFleetMetricDays(device_status.mtbf)}
                        label="MTBF"
                        className="total"
                        title="Mean time between impacting failures in the last 12 months. Same as Device View."
                      />
                    </div>
                  </div>
                  <div className="dashboard-subsection">
                    <div className="dashboard-subsection-title">Age</div>
                    <div className="dashboard-stats-summary">
                      {[
                        { label: 'New (<1yr)', value: device_status.age_new || 0, icon: '🌱', className: 'active' },
                        { label: '1–5 years', value: device_status.age_middle || 0, icon: '📈', className: 'total' },
                        { label: '>5 years', value: device_status.age_old || 0, icon: '⚠️', className: 'expiring' },
                      ].map((stat) => (
                        <StatsCard
                          key={stat.label}
                          icon={stat.icon}
                          number={stat.value}
                          label={stat.label}
                          className={stat.className}
                          title={`${stat.label} from installation or purchase date.`}
                        />
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <EmptyState
                  section="Devices"
                  message="No device data available yet."
                  icon="🖥️"
                />
              )}

              <div className="dashboard-subsection">
                <div className="dashboard-subsection-title">
                  Recent devices{device_list.length > 0 ? ` (${device_list.length})` : ''}
                </div>
                <div className="list-default-window-note" role="note">
                  <span className="list-default-window-note__icon" aria-hidden="true">ℹ️</span>
                  <span>
                    Preview of this department's Devices list, most recently updated first.
                    Click a row to open the device. Use the calendar icon for the same Maintenance Schedule as the Devices list.
                    {onNavigateToDevicesWithFilter && (
                      <>
                        {' '}
                        <button
                          type="button"
                          className="dashboard-inline-link"
                          onClick={() => onNavigateToDevicesWithFilter({})}
                        >
                          View full list
                        </button>
                      </>
                    )}
                    {' '}Health: Critical = overdue, non-compliant, or open complaints; Warning = due within 7 days.
                  </span>
                </div>
                {device_list.length > 0 ? (
                  <>
                    <div className="device-inventory-table-container">
                      <table className="device-inventory-table devices-preview-table">
                        <thead>
                          <tr>
                            <th className="col-device">Device</th>
                            <th className="col-device-id">Device ID</th>
                            <th className="col-department">Department</th>
                            <th className="col-location">Physical Location</th>
                            <th className="col-status">Health</th>
                            <th className="col-last-maintenance">Last</th>
                            <th className="col-next-maintenance">Maintenance Schedule</th>
                          </tr>
                        </thead>
                        <tbody>
                          {device_list.map((device) => {
                            const healthStatus = getDeviceHealthStatus(device);
                            const handleRowClick = () => {
                              if (device.id) {
                                onNavigateToDeviceView && onNavigateToDeviceView(device.id);
                              }
                            };
                            return (
                              <tr
                                key={device.id || device.device_identifier}
                                className={`device-inventory-row${device.id ? ' clickable' : ''}`}
                                onClick={handleRowClick}
                                title={device.id ? 'Click to view device' : undefined}
                              >
                                <td className="col-device">
                                  <span className="device-name">{device.name || 'Unknown Device'}</span>
                                </td>
                                <td className="col-device-id">
                                  {device.device_identifier ? (
                                    <DeviceIDBadge deviceId={device.device_identifier} size="small" />
                                  ) : (
                                    'N/A'
                                  )}
                                </td>
                                <td className="col-department">
                                  <span className="department-cell">
                                    <span className="department-icon-small" aria-hidden="true">
                                      {device.department_icon || '🏢'}
                                    </span>
                                    <span className="department-text">{device.department || 'N/A'}</span>
                                  </span>
                                </td>
                                <td className="col-location">{device.location || 'N/A'}</td>
                                <td className="col-status">
                                  <span className={`device-status-badge status-${healthStatus.status}`}>
                                    <span className="status-icon">{healthStatus.icon}</span>
                                    {healthStatus.label}
                                  </span>
                                </td>
                                <td className="col-last-maintenance">
                                  <div className="due-inline">
                                    <span className="due-date-value">
                                      {device.last_maintenance ? new Date(device.last_maintenance).toLocaleDateString() : 'Never'}
                                    </span>
                                    {device.last_maintenance_type && (
                                      <span className={`last-maintenance-type-badge ${getClassName(device.last_maintenance_type)}`}>
                                        {getLabel(device.last_maintenance_type)}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td
                                  className="col-next-maintenance"
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  {deviceHasMaintenanceSchedule(device) ? (
                                    <button
                                      type="button"
                                      className="next-due-icon-btn"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        setScheduleDevice(device);
                                      }}
                                      title="View maintenance schedule"
                                    >
                                      <span className="composite-icon">
                                        <span className="icon-base">📅</span>
                                      </span>
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      className="next-due-icon-btn"
                                      disabled
                                      style={{ opacity: 0.4, cursor: 'not-allowed' }}
                                      title="No maintenance schedule configured"
                                    >
                                      <span className="composite-icon">
                                        <span className="icon-base">📅</span>
                                      </span>
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {(inventory_stats.total_devices || 0) > device_list.length && (
                      <div style={{ textAlign: 'center', marginTop: '1rem', color: '#6b7280', fontSize: '0.9rem' }}>
                        Showing {device_list.length} of {inventory_stats.total_devices} devices
                        {onNavigateToDevicesWithFilter && (
                          <>
                            {' · '}
                            <button
                              type="button"
                              className="dashboard-inline-link"
                              onClick={() => onNavigateToDevicesWithFilter({})}
                            >
                              View full list
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <EmptyState
                    section="Recent devices"
                    message="No devices in your department."
                    icon="🖥️"
                  />
                )}
              </div>
            </Section>

            {/* ========================================
                SECTION 5: DEVICE COMPLIANCE
                PPM, calibration, and compliance rates
                ======================================== */}
            <Section id="device-compliance" title="✅ Device Compliance">
              {device_compliance && Object.keys(device_compliance).length > 0 ? (
                <>
                  <div className="list-default-window-note" role="note">
                    <span className="list-default-window-note__icon" aria-hidden="true">ℹ️</span>
                    <span>
                      PPM, calibration, and compliance for this department, matching the Devices list.
                      Rate colors: <strong>Green ≥80%</strong>, Amber 60–79%, Red &lt;60%.
                      Click a type card to view overdue devices.
                    </span>
                  </div>
                  <div className="dashboard-stats-summary" style={{ marginBottom: '1.25rem' }}>
                    <StatsCard
                      icon="📱"
                      number={device_compliance.total_count || 0}
                      label="Total Devices"
                      className="total"
                      onClick={onNavigateToDevicesWithFilter
                        ? () => onNavigateToDevicesWithFilter({})
                        : undefined}
                      title="Click to view all devices"
                    />
                    <StatsCard
                      icon="✅"
                      number={device_compliance.compliant_count || 0}
                      label="On Time"
                      className="active"
                      title="No overdue PPM, calibration, or compliance."
                    />
                    <StatsCard
                      icon="⚠️"
                      number={device_compliance.non_compliant_count || 0}
                      label="Overdue"
                      className={`open${Number(device_compliance.non_compliant_count) > 0 ? ' alert-elevated' : ''}`}
                      onClick={onNavigateToDevicesWithFilter
                        ? () => onNavigateToDevicesWithFilter({
                          ppmStatus: ['Overdue (0-30d)', 'Overdue (30+d)'],
                          calibrationStatus: ['Overdue (0-30d)', 'Overdue (30+d)'],
                          complianceStatus: ['Overdue (0-30d)', 'Overdue (30+d)'],
                        })
                        : undefined}
                      title="Overdue on PPM, calibration, or compliance. Click to view."
                    />
                  </div>
                  <div className="maintenance-type-grid">
                    {[
                      {
                        key: 'ppm',
                        label: 'PPM',
                        rate: device_compliance.compliance_rates?.ppm_rate,
                        filter: { ppmStatus: ['Overdue (0-30d)', 'Overdue (30+d)'] },
                      },
                      {
                        key: 'calibration',
                        label: 'Calibration',
                        rate: device_compliance.compliance_rates?.calibration_rate,
                        filter: { calibrationStatus: ['Overdue (0-30d)', 'Overdue (30+d)'] },
                      },
                      {
                        key: 'compliance',
                        label: 'Compliance',
                        rate: device_compliance.compliance_rates?.regulatory_rate,
                        filter: { complianceStatus: ['Overdue (0-30d)', 'Overdue (30+d)'] },
                      },
                    ].map((type) => {
                      const counts = device_compliance.by_type?.[type.key] || {};
                      const isNA = typeof type.rate === 'object' && type.rate?.value === null;
                      const percentage = isNA ? null : (type.rate || 0);
                      let rateClass = 'na';
                      if (percentage !== null) {
                        rateClass = percentage < 60 ? 'bad' : percentage < 80 ? 'warn' : 'ok';
                      }

                      return (
                        <div
                          key={type.key}
                          className="maintenance-type-card"
                          onClick={() => onNavigateToDevicesWithFilter && onNavigateToDevicesWithFilter(type.filter)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              onNavigateToDevicesWithFilter && onNavigateToDevicesWithFilter(type.filter);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          title={`Click to view overdue ${type.label.toLowerCase()}`}
                        >
                          <div className="maintenance-type-card__header">
                            <span className="maintenance-type-card__title">{type.label}</span>
                            {isNA ? (
                              <span className="maintenance-type-card__rate na" title={type.rate?.reason || 'No data available'}>N/A</span>
                            ) : (
                              <span className={`maintenance-type-card__rate ${rateClass}`}>
                                {formatPercentage(percentage)}
                              </span>
                            )}
                          </div>
                          <div className="maintenance-type-card__metrics">
                            <div className="maintenance-type-metric">
                              <div className="maintenance-type-metric__label">Overdue</div>
                              <div className="maintenance-type-metric__value overdue">{counts.overdue || 0}</div>
                            </div>
                            <div className="maintenance-type-metric">
                              <div className="maintenance-type-metric__label">Due Soon</div>
                              <div className="maintenance-type-metric__value due-soon">{counts.due_soon || 0}</div>
                            </div>
                            <div className="maintenance-type-metric">
                              <div className="maintenance-type-metric__label">Current</div>
                              <div className="maintenance-type-metric__value current">{counts.current || 0}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <EmptyState
                  section="Device Compliance"
                  message="No compliance data available yet."
                  icon="✅"
                />
              )}
            </Section>

            {/* ========================================
                SECTION 6: RECENT ACTIVITY
                Device management activities (last 7 days)
                ======================================== */}
            <Section id="recent-activity" title={`📊 Recent Activity${(recent_activity.length > 0 || (recent_activity_summary?.total || 0) > 0) ? ' (Last 7 Days)' : ''}`}>
              {(recent_activity.length > 0 || (recent_activity_summary?.total || 0) > 0) ? (
                <DepartmentRecentActivity
                  recentActivity={recent_activity}
                  summary={recent_activity_summary}
                  token={token}
                  user={user}
                  onNavigateToDeviceView={onNavigateToDeviceView}
                  onNavigateToMaintenanceHistory={onNavigateToMaintenanceHistory}
                  onNavigateToActivityLog={onNavigateToActivityLog}
                />
              ) : (
                <EmptyState
                  section="Recent Activity"
                  message="No department activity in the last 7 days."
                  icon="📊"
                />
              )}
            </Section>
          </DashboardLayout>
          {scheduleDevice && (
            <DeviceMaintenanceScheduleModal
              device={scheduleDevice}
              token={token}
              onClose={() => setScheduleDevice(null)}
            />
          )}
          </>
        );
      }}
    </BaseDashboard>
  );
};

export default CoordinatorDashboard;
